import { Typography } from '@sureco/design-system'
import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  message,
} from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import ClientSelect from '../../../../components/ClientSelect'
import { generateTestCensusFile } from '../api/census-enrollment-platform-api-client'
import { GenerateTestCensusResponseDto } from '../api/dto/generate-test-census.dto'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { SimpleCustomerDto } from '../../../api/persons/dto/simple-customer.dto'

interface BucketCounts {
  newRecord: number
  updateWithBenefit: number
  updateWithoutBenefit: number
  duplicate: number
}

const DEFAULT_COUNTS: BucketCounts = {
  newRecord: 5,
  updateWithBenefit: 2,
  updateWithoutBenefit: 2,
  duplicate: 1,
}

const BUCKET_LABELS: Array<{ key: keyof BucketCounts; label: string }> = [
  { key: 'newRecord', label: 'New records' },
  { key: 'updateWithBenefit', label: 'Updates (with benefits)' },
  { key: 'updateWithoutBenefit', label: 'Updates (without benefits)' },
  { key: 'duplicate', label: 'Duplicates' },
]

function GenerateTestCensusModal({
  open,
  onClose,
  onGenerated,
}: {
  open: boolean
  onClose: () => void
  onGenerated: () => void
}) {
  const { clients, error } = useCensusV2Clients()
  const [selectedClient, setSelectedClient] = useState<
    SimpleCustomerDto | undefined
  >()
  const [counts, setCounts] = useState<BucketCounts>(DEFAULT_COUNTS)
  const [persist, setPersist] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [result, setResult] = useState<GenerateTestCensusResponseDto | null>(
    null
  )

  useEffect(() => {
    if (!open) {
      setResult(null)
      setCounts(DEFAULT_COUNTS)
      setPersist(true)
    }
  }, [open])

  useEffect(() => {
    if (!error) {
      return
    }
    const msg = `Error loading companies: ${error.message}`
    console.error(msg)
    message.error(msg, 5)
  }, [error])

  const totalRequested = useMemo(
    () =>
      counts.newRecord +
      counts.updateWithBenefit +
      counts.updateWithoutBenefit +
      counts.duplicate,
    [counts]
  )

  const handleCountChange = (key: keyof BucketCounts, value: number | null) => {
    setCounts((prev) => ({
      ...prev,
      [key]: Math.max(0, Math.floor(value ?? 0)),
    }))
  }

  const downloadCsv = (res: GenerateTestCensusResponseDto) => {
    const binary = atob(res.csvBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    const blob = new Blob([bytes], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = res.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleGenerate = async () => {
    if (!selectedClient) {
      message.error('Please select a company first.')
      return
    }
    if (totalRequested === 0) {
      message.error('Set at least one bucket count above zero.')
      return
    }

    setIsGenerating(true)
    setResult(null)
    try {
      const res = await generateTestCensusFile({
        clientPublicKey: selectedClient.id,
        counts,
        persist,
      })
      setResult(res)
      if (res.warnings?.length) {
        res.warnings.forEach((w) => message.warning(w, 6))
      }
      if (persist) {
        message.success(`Generated census file ${res.fileName}`)
        onGenerated()
      } else {
        message.success('Census file generated (download only).')
        downloadCsv(res)
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? 'Unknown error'
      message.error(`Failed to generate census file: ${msg}`)
      console.error('Generate test census failed:', err)
    } finally {
      setIsGenerating(false)
    }
  }

  const actualRows = useMemo(() => {
    if (!result?.actualCounts) return []
    const c = result.actualCounts
    return [
      {
        bucket: 'New records (DRAFT_EMPLOYEE)',
        requested: result.requestedCounts.newRecord,
        actual: c[CensusFileRowTypeEnum.DRAFT_EMPLOYEE] ?? 0,
      },
      {
        bucket: 'Updates with benefits',
        requested: result.requestedCounts.updateWithBenefit,
        actual: c[CensusFileRowTypeEnum.UPDATE_EMPLOYEE_BENEFITS] ?? 0,
      },
      {
        bucket: 'Updates without benefits',
        requested: result.requestedCounts.updateWithoutBenefit,
        actual: c[CensusFileRowTypeEnum.UPDATE_EMPLOYEE_NO_BENEFITS] ?? 0,
      },
      {
        bucket: 'Duplicates',
        requested: result.requestedCounts.duplicate,
        actual: c[CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE] ?? 0,
      },
      {
        bucket: 'Errors',
        requested: 0,
        actual: c[CensusFileRowTypeEnum.ERROR] ?? 0,
      },
    ]
  }, [result])

  return (
    <Modal
      title="Generate Test Census File (Dev)"
      open={open}
      onOk={handleGenerate}
      onCancel={onClose}
      okText={persist ? 'Generate & Ingest' : 'Generate & Download'}
      confirmLoading={isGenerating}
      width={640}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message="Dev-only tool"
        description="Looks up the company's existing members to fabricate update/duplicate rows, and synthesizes brand-new employees. The actual bucket the backend assigns depends on each member's live benefits, so results may differ from the requested counts when the company lacks enough eligible members."
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Typography size={14}>Company:</Typography>
        <ClientSelect
          companies={clients.map(({ name, id }) => ({
            companyName: name,
            id,
          }))}
          defaultCompanyId={selectedClient?.id}
          onChange={(value: string) =>
            setSelectedClient(clients.find((c) => c.id === value))
          }
        />
      </div>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {BUCKET_LABELS.map(({ key, label }) => (
          <div
            key={key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography size={14}>{label}</Typography>
            <InputNumber
              min={0}
              max={1000}
              value={counts[key]}
              onChange={(value) => handleCountChange(key, value as number)}
            />
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography size={14}>
            Ingest as a real census file (otherwise download CSV only)
          </Typography>
          <Switch checked={persist} onChange={setPersist} />
        </div>
        <Typography size={12}>
          Total rows requested: {totalRequested}
        </Typography>
      </Space>

      {result && (
        <div style={{ marginTop: 20 }}>
          <Typography size={14} bold>
            Result
          </Typography>
          {result.persisted && result.importPublicKey && (
            <Typography size={12}>
              Import key: {result.importPublicKey} — file: {result.fileName}
            </Typography>
          )}
          {result.actualCounts ? (
            <Table
              size="small"
              pagination={false}
              style={{ marginTop: 8 }}
              rowKey="bucket"
              dataSource={actualRows}
              columns={[
                { title: 'Bucket', dataIndex: 'bucket', key: 'bucket' },
                {
                  title: 'Requested',
                  dataIndex: 'requested',
                  key: 'requested',
                  width: 110,
                },
                {
                  title: 'Actual',
                  dataIndex: 'actual',
                  key: 'actual',
                  width: 90,
                },
              ]}
            />
          ) : (
            <Typography size={12}>
              Planned — new: {result.plannedCounts.newRecord}, update w/
              benefit: {result.plannedCounts.updateWithBenefit}, update w/o
              benefit: {result.plannedCounts.updateWithoutBenefit}, duplicate:{' '}
              {result.plannedCounts.duplicate}
            </Typography>
          )}
          {!!result.csvBase64 && (
            <Button
              type="link"
              style={{ paddingLeft: 0, marginTop: 8 }}
              onClick={() => downloadCsv(result)}
            >
              Download generated CSV
            </Button>
          )}
        </div>
      )}
    </Modal>
  )
}

export default GenerateTestCensusModal
